import { styled, CSSObject, css } from '../theming';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import { fallbackTheme } from '../theming/fallbackTheme';
import { focusStyles } from '../utils/focusStyles';
import { LinkProps } from './types';
import { sizes, mappedColors } from './utils';
import { BabbelTheme } from '@lessonnine/design-tokens.lib/dist/json/theme';
import { ShortText } from '../Typography';

const inheritStyles = css`
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit,
  '&:hover': {
    color: inherit
  }
`;

const statesStyles = (theme: BabbelTheme, isActive?: boolean): CSSObject => ({
  '&:hover': {
    color: fallbackTheme(theme).color.brand.hover,
  },
  '&:active': {
    color: fallbackTheme(theme).color.brand.active,
  },
  '&:focus': {
    ...focusStyles({ type: 'pseudo', radius: 3, ringOffset: 1, theme }),
  },
  ...(isActive && {
    color: fallbackTheme(theme).color.brandInverse.onEnabled,
  }),
});

export const StyledLinkInherited = styled.span`
  ${inheritStyles},
`;

export const StyledLinkElement = styled(ShortText)`
  ${inheritStyles},
`;

export const StyledLinkIcon = styled.span<Pick<LinkProps, 'size' | 'alignIcon'>>`
  ${({ size, alignIcon }): CSSObject => ({
    ...getMargins({ alignIcon, size }),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      color: 'inherit',
    },
  })}
`;

const getMargins = ({ alignIcon, size }: Pick<LinkProps, 'size' | 'alignIcon'>): CSSObject => ({
  ...(alignIcon === 'end'
    ? { margin: `0 0 0 ${size === 'large' ? tokens.size.x3 : tokens.size.x2}` }
    : { margin: `0 ${size === 'large' ? tokens.size.x3 : tokens.size.x2} 0 0` }),
});

const sharedLinkStyles = (
  { color, size, underlined, inherited, disabled, weight, isActive }: LinkProps<'button' | 'a'>,
  theme: BabbelTheme
): CSSObject => {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    border: '0',
    padding: '0',
    margin: '0',
    textDecoration: !underlined ? 'none' : 'underline',
    textDecorationThickness: 'from-font',
    transition: 'color 0.1s ease-in-out 0s',
    background: 'none',
    appearance: 'none',
    whiteSpace: 'normal',
    textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: '1',
    verticalAlign: 'bottom',

    ...(inherited
      ? {
          fontFamily: 'inherit',
          fontSize: 'inherit',
          lineHeight: 'inherit',
          fontWeight: 'inherit',
          color: 'inherit',
        }
      : {
          fontFamily: tokens.font.family.grotesk,
          fontSize: size ? sizes[size].fontSize : sizes['default'].fontSize,
          lineHeight: size ? sizes[size].lineHeight : sizes['default'].lineHeight,
          fontWeight: weight === 'strong' ? '600' : '400',
          color: color && mappedColors(theme, color),
        }),
    ...(disabled && {
      color: fallbackTheme(theme).color.disabled.onSurfaceSubdued,
    }),
    ...(!disabled && {
      ...statesStyles(theme, isActive),
    }),
  };
};

export const StyledLink = styled.a<LinkProps<'a'>>`
  ${({ theme, ...props }): CSSObject => ({
    ...sharedLinkStyles(props, theme),
  })}
`;

export const StyledButtonLink = styled.button<LinkProps<'button'>>`
  ${({ theme, ...props }): CSSObject => ({
    ...sharedLinkStyles(props, theme),
  })}
`;
